import React, { useEffect, useState, FC } from 'react';
import { useSDMCartContext } from '@ldp/sdm-contexts';

import useGetUserPcoData from '@/hooks/use-get-user-pco-data';
import { useStore } from '@/store/useStore';
import { useUpdateCartItemQuantity } from '@/api/use-update-cart-item';
import { useAddToCart } from '@/api/use-add-to-cart';
import { LoggedInStatus } from '@/data/constants';
import { createCartGuidCookie, createCustomerCartGuidCookie, getLoggedInStatus, getCartProducts } from '@/utils';
import { ICartController } from './cart-controller.interface';
import { useHasHydrated, useTrackMediaPixelsAddToCart, useInitializeCart } from '@/hooks';

const CART_COUNT_STORAGE_KEY = 'sdm_cart_count';

const CartController: FC<ICartController> = ({ children }) => {
  const [cartFlag, setCartFlag] = useState(true);
  const [initialCartCount, setInitialCartCount] = useState<number | null>(null);
  const { cartCount, selectedProduct, isUpdateAction, populateEnteries, setIsInitialCartCountUpdated } =
    useSDMCartContext();
  const { productCode, quantity } = selectedProduct ? selectedProduct : { productCode: null, quantity: 0 };
  const { data: addToCartData, addToCart } = useAddToCart();
  const { createUpdateCartItemQuantityMutation } = useUpdateCartItemQuantity();
  const cartData = useStore(({ cartData }) => cartData);
  const hasHydrated = useHasHydrated();
  const { trackMediaPixelsAddToCart } = useTrackMediaPixelsAddToCart();

  const invokeAddToCartMutation = () => {
    addToCart({ productId: productCode, quantity: quantity });
  };

  useEffect(() => {
    if (!isUpdateAction && Object.entries(selectedProduct ?? {}).length > 0) {
      invokeAddToCartMutation();
    } else if (isUpdateAction) {
      createUpdateCartItemQuantityMutation(productCode, quantity);
    }
  }, [cartCount, isUpdateAction]);

  useEffect(() => {
    if (!hasHydrated) return;

    // Persist cartCount to localStorage for analytics tracking in non-React contexts
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(CART_COUNT_STORAGE_KEY, cartCount.toString());
      } catch (error) {
        console.warn('Failed to persist cart count to localStorage:', error);
      }
    }

    if (initialCartCount === null) {
      setInitialCartCount(cartCount);
    } else if (initialCartCount !== cartCount) {
      trackMediaPixelsAddToCart(cartCount);
    }
  }, [cartCount, hasHydrated, initialCartCount]);

  useEffect(() => {
    const data = addToCartData && addToCartData?.cart;
    const guid = data && data.id;
    if (guid && getLoggedInStatus() === LoggedInStatus.ANONYMOUS) {
      createCartGuidCookie(guid);
    }
    if (cartData?.data?.guid && getLoggedInStatus() === LoggedInStatus.CURRENT) {
      createCustomerCartGuidCookie(cartData?.data?.guid);
    }
  }, [addToCartData, cartData]);

  const cartEntries = cartData?.data?.entries || [];
  const cartGiftCardEntries = cartData?.data?.giftCardEntries || [];
  const cartProducts = getCartProducts([...cartEntries, ...cartGiftCardEntries]);

  useEffect(() => {
    if (cartProducts && cartFlag && Object.entries(selectedProduct ?? {}).length === 0) {
      populateEnteries(cartProducts);
      setCartFlag(false);
    }
  }, [cartProducts]);

  useEffect(() => {
    if (!cartFlag) {
      setIsInitialCartCountUpdated(true);
    }
  }, [cartFlag]);

  //getUserPcoData for analytics
  useGetUserPcoData();

  // Initialize cart for anonymous users on app load
  // This ensures cart_id is available for analytics tracking from the first add-to-cart
  useInitializeCart();

  return <>{children}</>;
};

export default CartController;
